import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const categories = ["Groceries", "Gas", "Dining", "Travel", "Online Shopping", "Entertainment", "Utilities", "Others"];

const CashbackRewardsCalculator = () => {
  const content = article.find((item) => item.id === 4);
  const [cards, setCards] = useState([
    {
      id: Date.now(),
      name: "Premium Cashback Card",
      type: "cashback",
      cashbackRates: { 
        Groceries: "6", 
        Gas: "3", 
        Dining: "4", 
        Travel: "2", 
        "Online Shopping": "3",
        Entertainment: "1",
        Utilities: "1",
        Others: "1" 
      },
      annualFee: "95",
      signupBonus: "200",
      promoOffer: "",
      pointsToDollar: "1",
      redemptionValue: "1",
      feeWaiverCondition: "Spend $1000 in first 3 months",
      pointsExpiry: "Never",
      rewardCaps: { 
        Groceries: "600", 
        Gas: null, 
        Dining: null, 
        Travel: null, 
        "Online Shopping": null,
        Entertainment: null,
        Utilities: null,
        Others: null 
      },
      quarterlyCategories: ["Groceries"],
      foreignTransactionFee: "0",
      cardNetwork: "Visa",
      creditNeeded: "Excellent"
    },
  ]);

  const [spending, setSpending] = useState(
    categories.reduce((acc, cat) => ({ 
      ...acc, 
      [cat]: cat === "Groceries" ? "800" : 
             cat === "Gas" ? "300" :
             cat === "Dining" ? "400" :
             cat === "Travel" ? "200" : "100" 
    }), {})
  );

  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [timeframe, setTimeframe] = useState("annual");
  const resultsRef = useRef();

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = cards.some(card => 
      Object.values(card.cashbackRates).some(rate => parseFloat(rate) > 0)
    );
    if (hasValidData) {
      setTimeout(() => {
        calculateRewards();
      }, 100);
    }
  }, [cards, spending, timeframe]);

  const handleCardChange = (id, field, value, subField = null) => {
    const newCards = cards.map(card => {
      if (card.id === id) {
        if (subField) {
          return {
            ...card,
            [field]: {
              ...card[field],
              [subField]: value
            }
          };
        } else {
          return {
            ...card,
            [field]: value
          };
        }
      }
      return card;
    });
    setCards(newCards);
  };

  const handleSpendingChange = (category, value) => {
    setSpending({ ...spending, [category]: value });
  };

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: `Card ${cards.length + 1}`,
      type: "cashback",
      cashbackRates: categories.reduce((acc, cat) => ({ ...acc, [cat]: "1" }), {}),
      annualFee: "0",
      signupBonus: "0",
      promoOffer: "",
      pointsToDollar: "1",
      redemptionValue: "1",
      feeWaiverCondition: "",
      pointsExpiry: "Never",
      rewardCaps: categories.reduce((acc, cat) => ({ ...acc, [cat]: null }), {}),
      quarterlyCategories: [],
      foreignTransactionFee: "3",
      cardNetwork: "Visa",
      creditNeeded: "Good"
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const calculateRewards = () => {
    const results = cards.map((card) => {
      let monthlyRewards = {};
      let totalMonthly = 0;
      let cappedAmounts = {};

      categories.forEach((cat) => {
        const spend = parseFloat(spending[cat]) || 0;
        let rate = parseFloat(card.cashbackRates[cat]) || 0;
        let reward = (spend * rate) / 100;
        
        // Apply reward caps if they exist
        const cap = card.rewardCaps[cat] ? parseFloat(card.rewardCaps[cat]) : null;
        if (cap && reward > cap) {
          cappedAmounts[cat] = (reward - cap).toFixed(2);
          reward = cap;
        } else {
          cappedAmounts[cat] = "0.00";
        }
        
        monthlyRewards[cat] = reward.toFixed(2);
        totalMonthly += reward;
      });

      // Calculate timeframe multiplier
      const timeframeMultiplier = timeframe === "annual" ? 12 : 
                                 timeframe === "quarterly" ? 3 : 1;

      const timeframeRewards = totalMonthly * timeframeMultiplier;
      const signupBonus = parseFloat(card.signupBonus || 0);
      const promoOffer = parseFloat(card.promoOffer || 0);
      const annualFee = parseFloat(card.annualFee || 0);

      const grossRewards = timeframeRewards + signupBonus + promoOffer;
      const netBenefit = grossRewards - (timeframe === "annual" ? annualFee : annualFee / 12 * timeframeMultiplier);

      // Calculate effective cashback rate
      const totalSpending = Object.values(spending).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const effectiveRate = totalSpending > 0 ? (totalMonthly / totalSpending * 100) : 0;

      return {
        name: card.name,
        monthlyRewards,
        totalMonthly: totalMonthly.toFixed(2),
        timeframeRewards: timeframeRewards.toFixed(2),
        grossRewards: grossRewards.toFixed(2),
        netBenefit: netBenefit.toFixed(2),
        effectiveRate: effectiveRate.toFixed(2),
        cappedAmounts,
        annualFee: annualFee.toFixed(2),
        signupBonus: signupBonus.toFixed(2),
        totalSpending: totalSpending.toFixed(2)
      };
    });

    setResults(results);
  };

  const resetCalculator = () => {
    setCards([{
      id: Date.now(),
      name: "Premium Cashback Card",
      type: "cashback",
      cashbackRates: { 
        Groceries: "6", 
        Gas: "3", 
        Dining: "4", 
        Travel: "2", 
        "Online Shopping": "3",
        Entertainment: "1",
        Utilities: "1",
        Others: "1" 
      },
      annualFee: "95",
      signupBonus: "200",
      promoOffer: "",
      pointsToDollar: "1",
      redemptionValue: "1",
      feeWaiverCondition: "Spend $1000 in first 3 months",
      pointsExpiry: "Never",
      rewardCaps: { 
        Groceries: "600", 
        Gas: null, 
        Dining: null, 
        Travel: null, 
        "Online Shopping": null,
        Entertainment: null,
        Utilities: null,
        Others: null 
      },
      quarterlyCategories: ["Groceries"],
      foreignTransactionFee: "0",
      cardNetwork: "Visa",
      creditNeeded: "Excellent"
    }]);
    setResults(null);
    setActiveCardIndex(0);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const rewardsComparisonData = results ? {
    labels: results.map(res => res.name),
    datasets: [
      {
        label: 'Net Benefit',
        data: results.map(res => parseFloat(res.netBenefit)),
        backgroundColor: '#38a169',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Gross Rewards',
        data: results.map(res => parseFloat(res.grossRewards)),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Annual Fee',
        data: results.map(res => -parseFloat(res.annualFee)),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const categoryRewardsData = results && results[0] ? {
    labels: categories,
    datasets: results.map((result, index) => ({
      label: result.name,
      data: categories.map(cat => parseFloat(result.monthlyRewards[cat])),
      backgroundColor: index === 0 ? '#F06201' : 
                      index === 1 ? '#FF8C42' : 
                      index === 2 ? '#A03B00' : '#FFB088',
      borderWidth: 2,
      borderColor: '#fff',
    }))
  } : null;

  const effectiveRateData = results ? {
    labels: results.map(res => res.name),
    datasets: [
      {
        label: 'Effective Cashback Rate (%)',
        data: results.map(res => parseFloat(res.effectiveRate)),
        backgroundColor: '#FF8C42',
        borderColor: '#FF8C42',
        borderWidth: 2,
      }
    ],
  } : null;

  const spendingDistributionData = {
    labels: categories,
    datasets: [
      {
        label: 'Monthly Spending',
        data: categories.map(cat => parseFloat(spending[cat]) || 0),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const totalMonthlySpending = Object.values(spending).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Cashback & Rewards Optimizer</h1>
          <p>Maximize your credit card rewards by comparing cashback rates and bonus categories</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Monthly Spending:</span>
              <strong>${totalMonthlySpending.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Cards Compared:</span>
              <strong>{cards.length}</strong>
            </div>
            <div className="summary-item">
              <span>Spending Categories:</span>
              <strong>{categories.length}</strong>
            </div>
            {results && results[0] && (
              <div className="summary-item">
                <span>Best Net Benefit:</span>
                <strong className="savings">
                  ${Math.max(...results.map(r => parseFloat(r.netBenefit))).toFixed(2)}
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Credit Card Rewards</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  <i className="fa-solid fa-plus"></i> Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Optimize your credit card rewards by matching spending categories with the highest cashback rates
              </div>
            </div>

            {/* Timeframe Selector */}
            <div className="timeframe-selector">
              <div className="input-group">
                <label>Analysis Timeframe</label>
                <select 
                  value={timeframe} 
                  onChange={(e) => setTimeframe(e.target.value)}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
            </div>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            {/* Spending Inputs */}
            <div className="spending-section">
              <h3>Your Monthly Spending</h3>
              <div className="input-grid">
                {categories.map(category => (
                  <div key={category} className="input-group">
                    <label>{category} ($)</label>
                    <input
                      type="number"
                      value={spending[category]}
                      onChange={(e) => handleSpendingChange(category, e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                ))}
              </div>
            </div><br/>

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Card Name</label>
                      <input
                        type="text"
                        value={card.name}
                        onChange={(e) => handleCardChange(card.id, "name", e.target.value)}
                        placeholder="Card Name"
                      />
                    </div>

                    <div className="input-group">
                      <label>Annual Fee ($)</label>
                      <input
                        type="number"
                        value={card.annualFee}
                        onChange={(e) => handleCardChange(card.id, "annualFee", e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Sign-up Bonus ($)</label>
                      <input
                        type="number"
                        value={card.signupBonus}
                        onChange={(e) => handleCardChange(card.id, "signupBonus", e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Promotional Offer ($)</label>
                      <input
                        type="number"
                        value={card.promoOffer}
                        onChange={(e) => handleCardChange(card.id, "promoOffer", e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Card Network</label>
                      <select 
                        value={card.cardNetwork} 
                        onChange={(e) => handleCardChange(card.id, "cardNetwork", e.target.value)}
                      >
                        <option value="Visa">Visa</option>
                        <option value="Mastercard">Mastercard</option>
                        <option value="American Express">American Express</option>
                        <option value="Discover">Discover</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label>Credit Needed</label>
                      <select 
                        value={card.creditNeeded} 
                        onChange={(e) => handleCardChange(card.id, "creditNeeded", e.target.value)}
                      >
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Fair">Fair</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>
                  </div>

                  {/* Cashback Rates Grid */}
                  <div className="cashback-section">
                    <h4>Cashback Rates (%)</h4>
                    <div className="input-grid">
                      {categories.map(category => (
                        <div key={category} className="input-group">
                          <label>{category} (%)</label>
                          <input
                            type="number"
                            value={card.cashbackRates[category]}
                            onChange={(e) => handleCardChange(card.id, "cashbackRates", e.target.value, category)}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Reward Caps */}
                  <div className="caps-section">
                    <h4>Reward Caps ($) - Optional</h4>
                    <div className="input-grid">
                      {categories.map(category => (
                        <div key={category} className="input-group">
                          <label>{category} Cap</label>
                          <input
                            type="number"
                            value={card.rewardCaps[category] || ""}
                            onChange={(e) => handleCardChange(card.id, "rewardCaps", e.target.value, category)}
                            placeholder="No cap"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateRewards}>
              Calculate Rewards
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Rewards Optimization Guide</h2>
            <div className="default-message">
              <p>Enter your monthly spending and credit card details to find the optimal rewards strategy.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Rewards Optimization Tips:</h3>
                <ul>
                  <li>Use different cards for different spending categories to maximize rewards</li>
                  <li>Consider rotating category cards for quarterly 5% bonuses</li>
                  <li>Always factor in annual fees when calculating net benefits</li>
                  <li>Look for sign-up bonuses that can significantly boost first-year rewards</li>
                  <li>Pay attention to reward caps and spending limits</li>
                  <li>Consider combining flat-rate cards with category-specific cards</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results && results.length > 0 ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Rewards Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-trophy"></i></div>
                  <h3>Best Card</h3>
                  <div className="summary-value">
                    {results.reduce((best, current) => 
                      parseFloat(current.netBenefit) > parseFloat(best.netBenefit) ? current : best
                    ).name}
                  </div>
                  <div className="summary-note">Highest net benefit</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Max Net Benefit</h3>
                  <div className="summary-value savings">
                    ${Math.max(...results.map(r => parseFloat(r.netBenefit))).toFixed(2)}
                  </div>
                  <div className="summary-note">{timeframe} benefit</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-percentage"></i></div>
                  <h3>Best Effective Rate</h3>
                  <div className="summary-value">
                    {Math.max(...results.map(r => parseFloat(r.effectiveRate))).toFixed(2)}%
                  </div>
                  <div className="summary-note">Cashback rate</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
                  <h3>Total Spending</h3>
                  <div className="summary-value">${totalMonthlySpending.toFixed(2)}</div>
                  <div className="summary-note">Monthly total</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Rewards Comparison</h3>
                    {rewardsComparisonData && <Bar data={rewardsComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Net Benefit Analysis:</strong> Compare total rewards minus annual fees across all cards.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Category Rewards Breakdown</h3>
                    {categoryRewardsData && <Bar data={categoryRewardsData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Category Optimization:</strong> See which cards perform best in each spending category.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Effective Cashback Rates</h3>
                    {effectiveRateData && <Bar data={effectiveRateData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>True Value:</strong> Actual cashback rate based on your specific spending patterns.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Spending Distribution</h3>
                    <Doughnut data={spendingDistributionData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Spending Analysis:</strong> Understand where your money goes each month.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.map((result, index) => (
                      <div key={index} className="result-card">
                        <h3>{result.name}</h3>
                        <div className="result-stats">
                          <div className="stat">
                            <label>Total Monthly Spending</label>
                            <span className="stat-value">${result.totalSpending}</span>
                          </div>
                          <div className="stat">
                            <label>Total Monthly Rewards</label>
                            <span className="stat-value">${result.totalMonthly}</span>
                          </div>
                          <div className="stat">
                            <label>Effective Cashback Rate</label>
                            <span className="stat-value">{result.effectiveRate}%</span>
                          </div>
                          <div className="stat">
                            <label>{timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Rewards</label>
                            <span className="stat-value">${result.timeframeRewards}</span>
                          </div>
                          <div className="stat">
                            <label>Sign-up Bonus</label>
                            <span className="stat-value">${result.signupBonus}</span>
                          </div>
                          <div className="stat">
                            <label>Annual Fee</label>
                            <span className="stat-value">-${result.annualFee}</span>
                          </div>
                          <div className="stat">
                            <label>Gross {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Rewards</label>
                            <span className="stat-value">${result.grossRewards}</span>
                          </div>
                          <div className="stat">
                            <label>Net {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Benefit</label>
                            <span className={`stat-value ${parseFloat(result.netBenefit) > 0 ? "savings" : ""}`}>
                              ${result.netBenefit}
                            </span>
                          </div>
                        </div>
                        
                        <div className="monthly-breakdown">
                          <h4>Monthly Rewards by Category</h4>
                          <div className="breakdown-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Category</th>
                                  <th>Spending</th>
                                  <th>Cashback Rate</th>
                                  <th>Rewards</th>
                                  <th>Capped Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {categories.map((category, i) => (
                                  <tr key={i}>
                                    <td>{category}</td>
                                    <td>${parseFloat(spending[category] || 0).toFixed(2)}</td>
                                    <td>{cards[index].cashbackRates[category]}%</td>
                                    <td>${result.monthlyRewards[category]}</td>
                                    <td>
                                      {parseFloat(result.cappedAmounts[category]) > 0 ? 
                                        `-$${result.cappedAmounts[category]}` : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Optimization Recommendations */}
                  <div className="scenario-comparison">
                    <h3>Optimization Recommendations</h3>
                    <div className="scenario-grid">
                      <div className="scenario-card">
                        <h4>Best Overall Card</h4>
                        <div className="scenario-stats">
                          <div className="stat">
                            <label>Card</label>
                            <span>{results.reduce((best, current) => 
                              parseFloat(current.netBenefit) > parseFloat(best.netBenefit) ? current : best
                            ).name}</span>
                          </div>
                          <div className="stat">
                            <label>Net Benefit</label>
                            <span className="savings">
                              ${Math.max(...results.map(r => parseFloat(r.netBenefit))).toFixed(2)}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Effective Rate</label>
                            <span>
                              {Math.max(...results.map(r => parseFloat(r.effectiveRate))).toFixed(2)}%
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="scenario-card">
                        <h4>Category Optimization</h4>
                        <div className="scenario-stats">
                          {categories.map(category => {
                            const bestCard = results.reduce((best, current) => 
                              parseFloat(current.monthlyRewards[category]) > parseFloat(best.monthlyRewards[category]) ? current : best
                            );
                            return (
                              <div key={category} className="stat">
                                <label>{category}</label>
                                <span>{bestCard.name} (${bestCard.monthlyRewards[category]})</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="scenario-card">
                        <h4>Multi-Card Strategy</h4>
                        <div className="scenario-stats">
                          <div className="stat">
                            <label>Potential Benefit</label>
                            <span className="savings">
                              ${categories.reduce((sum, category) => {
                                const maxReward = Math.max(...results.map(r => parseFloat(r.monthlyRewards[category])));
                                return sum + maxReward;
                              }, 0).toFixed(2)}/month
                            </span>
                          </div>
                          <div className="stat">
                            <label>Strategy</label>
                            <span>Use different cards for different categories</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your spending and credit card details to optimize your rewards strategy.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CashbackRewardsCalculator;